import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";

import translate from "../../../services/Translate";
import DstButton from "../../DstButton/DstButton";
import { CustomFilterDropdownProps } from "./CustomFilterDropdown.d";

const CustomFilterDropdown = ({
  activeCustomFilter,
  defaultFilters,
  language,
  onApplyFilter,
  resetActiveFilterOnDelete = false,
  storageKey,
}: CustomFilterDropdownProps) => {
  const [customFilters, setCustomFilters] = useState<Record<string, object>>({});
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!show) return;

    try {
      setCustomFilters(JSON.parse(localStorage.getItem(storageKey) || "{}"));
    } catch (error) {
      console.error("An error occurred with custom filters.", error);
      setCustomFilters({});
    }
  }, [show, storageKey]);

  const _applyDefaultFilter = () => {
    if (!defaultFilters) return;
    onApplyFilter(defaultFilters, "");
    setShow(false);
  };

  const _applySelectedFilter = (filterName: string) => {
    if (!customFilters[filterName]) return;
    onApplyFilter(customFilters[filterName], filterName);
    setShow(false);
  };

  const _deleteFilter = (event: React.MouseEvent, filterName: string) => {
    event.stopPropagation();
    if (
      !window.confirm(
        translate(language, "COMPONENTS.CUSTOM_FILTERS.SELECT_MODAL.CONFIRM_DELETE").replace(
          "{{FILTER_NAME}}",
          filterName
        )
      )
    )
      return;

    const updatedFilters = { ...customFilters };
    delete updatedFilters[filterName];
    localStorage.setItem(storageKey, JSON.stringify(updatedFilters));
    setCustomFilters(updatedFilters);

    if (resetActiveFilterOnDelete && activeCustomFilter === filterName && defaultFilters) {
      onApplyFilter(defaultFilters, "");
    }
  };

  return (
    <Dropdown onToggle={(nextShow) => setShow(nextShow)} show={show}>
      <DstButton
        btnClass="w-100 text-truncate"
        btnImageBefore={<span className="dst-icon-settings-04 me-1" />}
        clickFunction={() => setShow(!show)}
        tooltip={activeCustomFilter}
        tooltipPosition="top"
        value={`${translate(language, "COMPONENTS.CUSTOM_FILTERS.CURRENT_FILTER_SET")} ${
          activeCustomFilter || translate(language, "COMPONENTS.CUSTOM_FILTERS.DEFAULT_FILTER")
        }`}
        variant="light"
      />
      <Dropdown.Menu className="dropup">
        <Dropdown.Header>{translate(language, "COMPONENTS.CUSTOM_FILTERS.SELECT_MODAL.TITLE")}</Dropdown.Header>
        {defaultFilters && (
          <Dropdown.Item className="s-regular px-3 py-2" onClick={_applyDefaultFilter}>
            <span>{translate(language, "COMPONENTS.CUSTOM_FILTERS.DEFAULT_FILTER")}</span>
            {!activeCustomFilter && <span className="dst-icon-check-circle ms-2" />}
          </Dropdown.Item>
        )}
        {!Object.keys(customFilters).length ? (
          <Dropdown.ItemText className="text-muted xs-regular">
            {translate(language, "COMPONENTS.CUSTOM_FILTERS.SELECT_MODAL.NO_FILTERS")}
          </Dropdown.ItemText>
        ) : (
          Object.keys(customFilters).map((filterName) => (
            <div className="d-flex align-items-center" key={filterName}>
              <Dropdown.Item
                className="s-regular flex-grow-1 text-truncate px-3 py-2"
                onClick={() => _applySelectedFilter(filterName)}
              >
                {filterName}
                {activeCustomFilter === filterName && <span className="dst-icon-check-circle ms-2" />}
              </Dropdown.Item>
              <DstButton
                btnClass="me-2 p-1 text-danger text-decoration-none"
                btnImageBefore={<span className="dst-icon-trash-01" />}
                clickFunction={(event: React.MouseEvent) => _deleteFilter(event, filterName)}
                tooltip={translate(language, "GLOBAL.DELETE")}
                value=""
                variant="link"
              />
            </div>
          ))
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CustomFilterDropdown;
